import type { Coordinate } from "ol/coordinate";
import Feature from "ol/Feature";
import LineString from "ol/geom/LineString";
import Point from "ol/geom/Point";
import { type ProjectionLike, transform } from "ol/proj";
import { getUid } from "ol/util";
import { getAPIBaseUrl, getJwtToken } from "./environment";
import { getStopOverId, type StopOver } from "./HikingEditor";
import { pairwise } from "./utils";
import type { Discipline } from "./walking";

export const hikingTypes = ["hiking", "hiking_activity", "hiking_proposition"] as const;
export type HikingType = (typeof hikingTypes)[number];
export type Organisation =
  | "Aargauer Wanderwege"
  | "Appenzell Ausserrhoder Wanderwege"
  | "Appenzeller Wanderwege AI"
  | "Wanderwege beider Basel"
  | "Berner Wanderwege"
  | "Fribourg Rando"
  | "Fachorganisation"
  | "Genève Rando"
  | "Glarner Wanderwege"
  | "Wanderwege Graubünden"
  | "Jura Rando"
  | "Luzerner Wanderwege"
  | "Neuchâtel Rando"
  | "Nidwaldner Wanderwege"
  | "Obwaldner Wanderwege"
  | "St. Galler Wanderwege"
  | "Schaffhauser Wanderwege"
  | "Schwyzer Wanderwege"
  | "Solothurner Wanderwege"
  | "Thurgauer Wanderwege"
  | "TicinoSentieri"
  | "Urner Wanderwege"
  | "Valrando"
  | "Vaud Rando"
  | "Zuger Wanderwege"
  | "Zürcher Wanderwege"
  | "Liechtensteinische Wanderwege"
  | "Schweizer Wanderwege";

export type Category =
  | "alpine hike"
  | "mountain hike"
  | "snow shoe hike"
  | "hike"
  | "winter hike"
  | "schooltrip with hike"
  | "schooltrip without hike";

export type SACScale = "BLACK" | "BLUE" | "RED" | "T1" | "T2" | "T3" | "T4" | "T5" | "T6" | "WT1" | "WT2" | "WT3";
export type PhysicalDemand = "low" | "medium" | "high";

type ServerHikingWrite = {
  uuid: string;
  control_points_with_stopovers: ServerControlPoint[];
  coordinates: Coordinate[][];
};

export type ServerHiking = ServerHikingWrite & {
  discipline: Discipline;
  organisation?: Organisation;
  physical_demand?: PhysicalDemand;
  custom_physical_demand?: boolean;
  height_difference_upwards?: number;
  custom_height_difference_upwards?: boolean;
  height_difference_downwards?: number;
  custom_height_difference_downwards?: boolean;
};

export type UnsavedHiking = {
  control_points_with_stopovers: ServerControlPoint[];
  coordinates: Coordinate[][];
  discipline: Discipline;
};

type ServerControlPointWrite = {
  coordinates: Coordinate;
  name: string | null;
  features: unknown[] | null;
  distance: number;
  duration: number;
};

export type ServerControlPoint = ServerControlPointWrite & {
  vertex_index: number;
};

const SERVER_PROJECTION = "EPSG:4326";

/**
 * Returns the list of hiking of the current user.
 */
export async function getUserHiking(): Promise<ServerHiking> {
  const response = await fetch(`${getAPIBaseUrl()}/hiking`, {
    headers: {
      Authorization: `Bearer ${getJwtToken()}`,
    },
  });
  return await response.json();
}

export async function fetchHiking(uuid: string, hikingType: HikingType): Promise<ServerHiking> {
  const response = await fetch(`${getAPIBaseUrl()}/${hikingType}/${uuid}`, {
    headers: {
      Authorization: `Bearer ${getJwtToken()}`,
    },
  });
  return await response.json();
}

export function readFeatures(
  hiking: ServerHiking | UnsavedHiking,
  projection: ProjectionLike,
): Feature<LineString | Point>[] {
  const features: Feature<LineString | Point>[] = [];

  let vertex_count = 0;
  hiking.coordinates.forEach((coordinates, part) => {
    const control_points = hiking.control_points_with_stopovers.filter(
      (point) => point.vertex_index >= vertex_count && point.vertex_index < vertex_count + coordinates.length,
    );
    // change the vertex_index to be relative to the part
    control_points.forEach((point) => {
      point.vertex_index -= vertex_count;
    });

    control_points.forEach((point, index) => {
      const feature = new Feature({
        geometry: new Point(point.coordinates).transform(SERVER_PROJECTION, projection),
        type: "controlPoint",
        part: part,
        snapped: true,
        index: index,
        stopover: point.name !== null,
        sww_name: point.name,
        sww_features: point.features,
        sww_duration: point.duration,
        sww_distance: point.distance,
      });
      feature.set("sww_id", getUid(feature));
      if (index === 0) {
        feature.set("subtype", "first");
      } else if (index === control_points.length - 1) {
        feature.set("subtype", "last");
      }
      features.push(feature);
    });

    for (const [previous, current] of pairwise(control_points)) {
      // 'current.vertex_index +1' to include the current vertex in the segment
      const segment_coordinates = coordinates.slice(previous.vertex_index, current.vertex_index + 1);
      const segment = new Feature({
        geometry: new LineString(segment_coordinates).transform(SERVER_PROJECTION, projection),
        type: "segment",
        part: part,
        snapped: true,
      });
      features.push(segment);
    }

    vertex_count += coordinates.length;
  });
  return features;
}

export function writeFeatures(
  all_segments: readonly Feature<LineString>[][],
  all_control_points: readonly Feature<Point>[][],
  stopovers: Record<string, StopOver>,
  projection: ProjectionLike,
): any {
  const result = {
    control_points_with_stopovers: [] as unknown[],
    coordinates: [] as Coordinate[][],
  };

  if (all_segments.length !== all_control_points.length) {
    throw new Error("Not the same number of segments and control points");
  }
  for (let i = 0; i < all_segments.length; i++) {
    const segments = all_segments[i];
    const control_points = all_control_points[i];

    const segments_coordinates = segments.map((segment) => {
      return segment
        .get("profile")
        .map((c: Coordinate) => c.slice(0, 3))
        .map((c: Coordinate) => transform(c, projection, SERVER_PROJECTION));
    });

    // instead of using the point geometry, we use the segment geometry to always have XYZ coordinates
    const points_coordinates = segments_coordinates.map((coordinates) => coordinates.at(0));
    points_coordinates.push(segments_coordinates.at(-1)?.at(-1));

    const points = control_points.map((point, index) => {
      const stopover = stopovers[getStopOverId(point)];
      if (stopover) {
        if (stopover.properties.customName === null && stopover.properties.swissNames.length === 0) {
          throw new Error("A stopover must have a name");
        }
        return {
          name: stopover.properties.customName ?? stopover.properties.swissNames[0],
          features: stopover.properties.features,
          coordinates: points_coordinates[index],
          duration: stopover.properties.customDuration,
          distance: stopover.properties.customDistance,
        };
      }
      return {
        coordinates: points_coordinates[index],
      };
    });

    result.control_points_with_stopovers.push(...points);
    result.coordinates.push(segments_coordinates.flat());
  }
  return result;
}

export async function createHiking(
  hikingType: HikingType,
  geometries: ServerHikingWrite,
  properties: Record<string, unknown>,
): Promise<ServerHiking> {
  const response = await fetch(`${getAPIBaseUrl()}/${hikingType}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getJwtToken()}`,
    },
    body: JSON.stringify({ ...geometries, ...properties }),
  });
  return await response.json();
}

export async function updateHiking(
  uuid: string,
  hikingType: HikingType,
  geometries: ServerHikingWrite,
  properties: Record<string, unknown>,
): Promise<ServerHiking> {
  const response = await fetch(`${getAPIBaseUrl()}/${hikingType}/${uuid}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getJwtToken()}`,
    },
    body: JSON.stringify({ ...geometries, ...properties }),
  });
  return await response.json();
}
